import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { type AccessPolicyProps, type AllowedOfflineFn } from './ProtectedRoute.types';
import { useAccessPolicy } from './useAccessPolicy';
import { useForcedRedirect } from './useForcedRedirect';
import { useEnterOrLeaveOfflineMode } from './useEnterOrLeaveOfflineMode';

type ProtectedRouteProps = {
    allowedWhenOnboardingIncomplete?: boolean;
    allowedOffline?: boolean | AllowedOfflineFn;
    children: ReactNode;
} & AccessPolicyProps;

export function ProtectedRoute({
    allowedWhenOnboardingIncomplete = false,
    allowedOffline = false,
    accessPolicy,
    accessPolicyFn,
    children,
}: ProtectedRouteProps) {
    const accessPolicyProps = accessPolicy === 'custom' ? { accessPolicy, accessPolicyFn } : { accessPolicy };
    const accessRedirect = useAccessPolicy(accessPolicyProps);
    const offlineRedirect = useEnterOrLeaveOfflineMode(allowedOffline);
    const forcedRedirect = useForcedRedirect({ allowedWhenOnboardingIncomplete, children });

    // Order matters here, we only want to redirect to the return result of forcedRedirect if we get through the access and offline checks.
    const redirect = accessRedirect || offlineRedirect || forcedRedirect;

    if (redirect) {
        return <Navigate to={redirect} replace />;
    }

    return <>{children}</>;
}
